import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline'
import { isCurrentMonthOrLater, isWholeMonth, shiftMonth } from './coveragePeriod'
import { formatMonth, formatShortDate } from './coverageFormat'
import { CustomRangeForm } from './CustomRangeForm'
import { stepButton, textButton, unavailableStepButton } from './coverageStyles'
import { PERIOD_I18N } from './i18nKeys'

export const CoveragePeriodPicker = ({ period, today, onChange }) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const [open, setOpen] = useState(false)
  const customToggle = useRef(null)

  const label = isWholeMonth(period, today)
    ? formatMonth(period.from, language)
    : t(`${PERIOD_I18N}.range`, { from: formatShortDate(period.from, language), to: formatShortDate(period.to, language) })
  const atCurrentMonth = isCurrentMonthOrLater(period.from, today)

  const apply = next => {
    onChange({ from: next.from, to: next.to })
    setOpen(false)
    customToggle.current.focus()
  }

  return (
    <div role="group" aria-label={t(`${PERIOD_I18N}.group`)} className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <button type="button" className={stepButton} aria-label={t(`${PERIOD_I18N}.previous_month`)} onClick={() => onChange(shiftMonth(period, -1, today))}>
          <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
        </button>
        <span aria-live="polite" className="min-w-15 whitespace-nowrap text-center text-sm font-semibold text-gray-900">
          {label}
        </span>
        <button
          type="button"
          className={atCurrentMonth ? unavailableStepButton : stepButton}
          aria-label={t(`${PERIOD_I18N}.next_month`)}
          aria-disabled={atCurrentMonth ? 'true' : undefined}
          onClick={atCurrentMonth ? undefined : () => onChange(shiftMonth(period, 1, today))}
        >
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
        </button>
        <button ref={customToggle} type="button" className={textButton} aria-expanded={open} onClick={() => setOpen(!open)}>
          {t(`${PERIOD_I18N}.custom`)}
        </button>
      </div>
      {open && <CustomRangeForm key={`${period.from}/${period.to}`} period={period} today={today} onApply={apply} />}
    </div>
  )
}
